import { PASSWORD_MIN_LENGTH, resetPasswordInputSchema } from '@dovefiniscono/shared';
import { Link, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { cx } from '../../lib/cx';
import { type FieldErrors, validate } from '../../lib/form';
import { useSession } from '../../lib/session';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import styles from './Auth.module.css';
import { AuthLayout } from './AuthLayout';

export function ResetPasswordPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const session = useSession();
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = validate(resetPasswordInputSchema, {
      token: token ?? '',
      password: new FormData(event.currentTarget).get('password'),
    });
    setErrors(result.errors ?? {});
    setFormError(result.errors?.token ? 'Il link non è valido: richiedine uno nuovo.' : null);
    if (!result.data) return;
    setLoading(true);
    try {
      await api.auth.resetPassword(result.data);
      // Every device was signed out, this one included.
      if (session.status === 'signedIn') await session.signOut();
      setDone(true);
    } catch (error) {
      setFormError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <AuthLayout title="Password aggiornata" subtitle="Ora puoi accedere con la nuova password.">
        <div className={styles.links}>
          <Link to="/login">Vai all'accesso</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Nuova password" subtitle="Per sicurezza uscirai da tutti i dispositivi.">
      <form className={styles.fields} onSubmit={submit} noValidate>
        <TextField
          label="Nuova password"
          name="password"
          type="password"
          autoComplete="new-password"
          autoFocus
          hint={`Almeno ${PASSWORD_MIN_LENGTH} caratteri.`}
          error={errors.password}
        />
        {formError ? (
          <p className={cx(styles.notice)} role="alert">
            {formError}
          </p>
        ) : null}
        <Button type="submit" variant="primary" className={styles.submit} loading={loading}>
          Salva la password
        </Button>
      </form>
      <div className={styles.links}>
        <Link to="/password-dimenticata">Richiedi un nuovo link</Link>
      </div>
    </AuthLayout>
  );
}
